import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MenuItem } from '@/types/menu';

export interface CartItem {
  item: MenuItem;
  quantity: number;
  specialInstructions?: string;
}

export interface CartState {
  items: CartItem[];
  selectedTableId: string | null;
  selectedTableNumber: string | null;
  selectedTableName: string | null;
  storeId: string | null;
  
  // Actions
  addItem: (item: MenuItem, quantity?: number, specialInstructions?: string) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  updateSpecialInstructions: (itemId: string, instructions: string) => void;
  clearCart: () => void;
  setTable: (tableId: string, tableNumber: string, tableName: string) => void;
  setStore: (storeId: string) => void;
  
  // Computed values
  getTotalItems: () => number;
  getTotalAmount: () => number;
  getItemQuantity: (itemId: string) => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      selectedTableId: null,
      selectedTableNumber: null,
      selectedTableName: null,
      storeId: null,

      addItem: (item: MenuItem, quantity = 1, specialInstructions = '') => {
        set((state) => {
          const existingItemIndex = state.items.findIndex(
            (cartItem) => cartItem.item.id === item.id
          );

          if (existingItemIndex >= 0) {
            // Update existing item
            const updatedItems = [...state.items];
            updatedItems[existingItemIndex] = {
              ...updatedItems[existingItemIndex],
              quantity: updatedItems[existingItemIndex].quantity + quantity,
              specialInstructions: specialInstructions || updatedItems[existingItemIndex].specialInstructions,
            };
            return { items: updatedItems };
          } else {
            // Add new item
            return {
              items: [
                ...state.items,
                {
                  item,
                  quantity,
                  specialInstructions,
                },
              ],
            };
          }
        });
      },

      removeItem: (itemId: string) => {
        set((state) => ({
          items: state.items.filter((cartItem) => cartItem.item.id !== itemId),
        }));
      },

      updateQuantity: (itemId: string, quantity: number) => {
        if (quantity <= 0) {
          get().removeItem(itemId);
          return;
        }

        set((state) => ({
          items: state.items.map((cartItem) =>
            cartItem.item.id === itemId
              ? { ...cartItem, quantity }
              : cartItem
          ),
        }));
      },

      updateSpecialInstructions: (itemId: string, instructions: string) => {
        set((state) => ({
          items: state.items.map((cartItem) =>
            cartItem.item.id === itemId
              ? { ...cartItem, specialInstructions: instructions }
              : cartItem
          ),
        }));
      },

      clearCart: () => {
        set({
          items: [],
          selectedTableId: null,
          selectedTableNumber: null,
          selectedTableName: null,
        });
      },

      setTable: (tableId: string, tableNumber: string, tableName: string) => {
        set({
          selectedTableId: tableId,
          selectedTableNumber: tableNumber,
          selectedTableName: tableName,
        });
      },

      setStore: (storeId: string) => {
        set({ storeId });
      },

      getTotalItems: () => {
        return get().items.reduce((total, cartItem) => total + cartItem.quantity, 0);
      },

      getTotalAmount: () => {
        return get().items.reduce(
          (total, cartItem) => total + (Number(cartItem.item.price) || 0) * cartItem.quantity,
          0
        );
      },

      getItemQuantity: (itemId: string) => {
        const item = get().items.find((cartItem) => cartItem.item.id === itemId);
        return item ? item.quantity : 0;
      },
    }),
    {
      name: 'waiter-cart-storage',
      partialize: (state) => ({
        items: state.items,
        selectedTableId: state.selectedTableId,
        selectedTableNumber: state.selectedTableNumber,
        selectedTableName: state.selectedTableName,
        storeId: state.storeId,
      }),
    }
  )
); 